import { useModal } from '@minecraft.modal';
import React, { useCallback, useMemo } from 'react';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { SocialMediaPlatform } from '@minecraft.graphql-operations';
import { helpers, useDeviceAndBrowserInfo } from '@minecraft.utils';
import { useTranslation } from '@blocs.i18n';
import { gqlSocialMediaOverview } from '@minecraft.graphql-types';
import { FlexRow } from '@minecraft.atoms';
import { InstagramButton } from './InstagramButton';
import { UnifiedConsentModal, ConsentType, UnifiedDisconnectModal, UnifiedReconnectModal } from '../SocialActionModal';

interface InstagramProps {
  deleteAllSocialData: () => Promise<void>;
  disconnectSocialAccount: () => Promise<void>;
  eventSourcePage?: string;
  handleInstagramAuth: () => Promise<void>;
  mutationLoading: boolean;
  instagramDetails: gqlSocialMediaOverview;
}

export const InstagramSocial = ({
  deleteAllSocialData,
  disconnectSocialAccount,
  eventSourcePage,
  handleInstagramAuth,
  mutationLoading,
  instagramDetails,
}: InstagramProps) => {
  const { t } = useTranslation();

  const followerCount = instagramDetails?.metrics?.followerCount;
  const profileLink = instagramDetails?.link;
  const isInstagramConnected = instagramDetails?.isTokenValid;

  const instagramButtonLabel = useMemo(() => {
    if (isInstagramConnected) {
      return t('common:profile.info.social.instagram.followerCount', {
        formattedCount: helpers.formatMetricCount(followerCount, true),
      });
    }
    if (isInstagramConnected === false) {
      return t('common:profileEdit.social.reconnectButton');
    }
    return t('common:customizeSubmission.addYourSocial.button.label', { socialMediaPlatform: 'Instagram' });
  }, [isInstagramConnected, t, followerCount]);

  const { isOpen: isConsentModalOpen, toggleModal: toggleConsentModal, close: closeConsentModal } = useModal();
  const { isOpen: isDisconnectModalOpen, toggleModal: toggleDisconnectModal, close: closeDisconnectModal } = useModal();
  const { isOpen: isReconnectModalOpen, toggleModal: toggleReconnectModal, close: closeReconnectModal } = useModal();
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();

  const onConnect = useCallback(async () => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_SOCIAL_ADD_LINK,
      data: {
        socialPlatform: SocialMediaPlatform.Instagram,
        timestamp: new Date().toISOString(),
        device: deviceInfo,
        browser: browserInfo,
        location: eventSourcePage,
      },
    });
    await handleInstagramAuth();
    closeConsentModal();
  }, [deviceInfo, browserInfo, eventSourcePage, handleInstagramAuth, closeConsentModal]);

  const onReconnect = useCallback(() => {
    toggleConsentModal();
    closeReconnectModal();
  }, [closeReconnectModal, toggleConsentModal]);

  return (
    <>
      <FlexRow
        alignItems="center"
        justifyContent="center"
        bgColor="cardLightContrast"
        borderRadius="md"
        className="cn_atom_p-2"
        mdProps={{ w: 'max', className: 'cn_atom_p-2' }}
      >
        <InstagramButton
          label={instagramButtonLabel}
          profileLink={profileLink}
          onConnect={() => {
            toggleConsentModal();
            eventSourcePage === 'submission-page' &&
              captureHeapEvent({
                name: HEAP_EVENT_NAMES.TALENT_SOCIAL_CLICK_CONNECT_INSTAGRAM,
                data: {
                  socialPlatform: SocialMediaPlatform.Instagram,
                  timestamp: new Date().toISOString(),
                  device: deviceInfo,
                  browser: browserInfo,
                  location: eventSourcePage,
                },
              });
          }}
          onDisconnect={toggleDisconnectModal}
          showCloseButton={!!profileLink && isInstagramConnected}
          onReconnect={toggleReconnectModal}
          isInstagramConnected={isInstagramConnected}
        />
      </FlexRow>
      {isConsentModalOpen && (
        <UnifiedConsentModal
          close={closeConsentModal}
          consentType={ConsentType.InstagramConsent}
          onPrimaryAction={onConnect}
          mutationLoading={mutationLoading}
          data-testid="instagram-auth-modal"
        />
      )}
      {isDisconnectModalOpen && (
        <UnifiedDisconnectModal
          platform="Instagram"
          close={closeDisconnectModal}
          deleteAllSocialData={deleteAllSocialData}
          disconnectSocialAccount={disconnectSocialAccount}
          mutationLoading={mutationLoading}
          data-testid="disconnect-instagram-modal"
        />
      )}
      {isReconnectModalOpen && (
        <UnifiedReconnectModal close={closeReconnectModal} onReconnect={onReconnect} platform="Instagram" />
      )}
    </>
  );
};
